/**
 * At Risk Tasks Dashboard
 * Displays tasks that need attention with health indicators
 */

import React, { useState } from 'react';
import { Task, User } from '../types';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Progress } from './ui/progress';
import TaskHealthBadge from './TaskHealthBadge';
import {
  getAtRiskTasks,
  getProjectHealth,
  sortTasksByUrgency,
  getTasksNeedingUpdate,
  getTaskHealth,
} from '../services/notificationService';
import {
  AlertTriangle,
  AlertCircle,
  Clock,
  Ban,
  TrendingUp,
  Filter,
  User as UserIcon,
  Calendar,
  ExternalLink,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import {
  WIDGET_ALERT_DANGER_CLASS,
  WIDGET_ALERT_DANGER_TEXT_CLASS,
  WIDGET_ALERT_PURPLE_CLASS,
  WIDGET_ALERT_PURPLE_TEXT_CLASS,
  WIDGET_ALERT_SUCCESS_TEXT_CLASS,
  WIDGET_ALERT_WARNING_CLASS,
  WIDGET_ALERT_WARNING_TEXT_CLASS,
  WIDGET_MUTED_TEXT_CLASS,
  WIDGET_PANEL_CLASS,
} from '../constants/widgetTheme';

interface AtRiskTasksDashboardProps {
  tasks: Task[];
  users: User[];
  projectId?: string;
  showAll?: boolean;
}

export default function AtRiskTasksDashboard({
  tasks,
  users,
  projectId,
  showAll = false,
}: AtRiskTasksDashboardProps) {
  const navigate = useNavigate();
  const [filter, setFilter] = useState<'all' | 'overdue' | 'at-risk' | 'needs-update' | 'blocked'>('all');

  const projectHealth = getProjectHealth(tasks);
  const atRiskTasks = getAtRiskTasks(tasks);
  const needsUpdateTasks = getTasksNeedingUpdate(tasks);
  const sortedTasks = sortTasksByUrgency(showAll ? tasks : atRiskTasks);

  const filteredTasks = sortedTasks.filter(task => {
    if (filter === 'all') return true;
    const health = getTaskHealth(task);
    return health.status === filter;
  });

  const toPercent = (value: number) =>
    projectHealth.total > 0 ? (value / projectHealth.total) * 100 : 0;

  const getUserName = (userId: string) => {
    const user = users.find(u => u.id === userId);
    return user?.name || 'Unassigned';
  };

  const handleTaskClick = (task: Task) => {
    navigate(`/projects/${task.projectId}/tasks/${task.id}`);
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className={`${WIDGET_PANEL_CLASS} ${WIDGET_ALERT_DANGER_CLASS}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${WIDGET_ALERT_DANGER_TEXT_CLASS}`}>Overdue</p>
                <p className={`mt-1 text-3xl font-bold ${WIDGET_ALERT_DANGER_TEXT_CLASS}`}>{projectHealth.overdue}</p>
              </div>
              <AlertCircle className={`h-8 w-8 ${WIDGET_ALERT_DANGER_TEXT_CLASS}`} />
            </div>
          </CardContent>
        </Card>

        <Card className={`${WIDGET_PANEL_CLASS} ${WIDGET_ALERT_WARNING_CLASS}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>At Risk</p>
                <p className={`mt-1 text-3xl font-bold ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>{projectHealth.atRisk}</p>
              </div>
              <AlertTriangle className={`h-8 w-8 ${WIDGET_ALERT_WARNING_TEXT_CLASS}`} />
            </div>
          </CardContent>
        </Card>

        <Card className={`${WIDGET_PANEL_CLASS} ${WIDGET_ALERT_WARNING_CLASS}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>Needs Update</p>
                <p className={`mt-1 text-3xl font-bold ${WIDGET_ALERT_WARNING_TEXT_CLASS}`}>{projectHealth.needsUpdate}</p>
              </div>
              <Clock className={`h-8 w-8 ${WIDGET_ALERT_WARNING_TEXT_CLASS}`} />
            </div>
          </CardContent>
        </Card>

        <Card className={`${WIDGET_PANEL_CLASS} ${WIDGET_ALERT_PURPLE_CLASS}`}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className={`text-sm font-medium ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`}>Blocked</p>
                <p className={`mt-1 text-3xl font-bold ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`}>{projectHealth.blocked}</p>
              </div>
              <Ban className={`h-8 w-8 ${WIDGET_ALERT_PURPLE_TEXT_CLASS}`} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Project Health Overview */}
      <Card className={WIDGET_PANEL_CLASS}>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="w-5 h-5" />
            Project Health Overview
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="flex items-center justify-between">
              <span className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>Total Tasks</span>
              <span className="font-semibold">{projectHealth.total}</span>
            </div>
            <div className="space-y-2">
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-destructive">Overdue</span>
                  <span>{projectHealth.overdue}</span>
                </div>
                <Progress
                  value={toPercent(projectHealth.overdue)}
                  className="h-2 [&::-webkit-progress-value]:bg-destructive/10 [&::-moz-progress-bar]:bg-destructive/10"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-chart-4">Blocked</span>
                  <span>{projectHealth.blocked}</span>
                </div>
                <Progress
                  value={toPercent(projectHealth.blocked)}
                  className="h-2 [&::-webkit-progress-value]:bg-chart-4/10 [&::-moz-progress-bar]:bg-chart-4/10"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-chart-5">At Risk</span>
                  <span>{projectHealth.atRisk}</span>
                </div>
                <Progress
                  value={toPercent(projectHealth.atRisk)}
                  className="h-2 [&::-webkit-progress-value]:bg-chart-5/10 [&::-moz-progress-bar]:bg-chart-5/10"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-chart-5">Needs Update</span>
                  <span>{projectHealth.needsUpdate}</span>
                </div>
                <Progress
                  value={toPercent(projectHealth.needsUpdate)}
                  className="h-2 [&::-webkit-progress-value]:bg-chart-5/10 [&::-moz-progress-bar]:bg-chart-5/10"
                />
              </div>
              <div className="space-y-1">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-chart-2">Healthy</span>
                  <span>{projectHealth.healthy}</span>
                </div>
                <Progress
                  value={toPercent(projectHealth.healthy)}
                  className="h-2 [&::-webkit-progress-value]:bg-chart-2/10 [&::-moz-progress-bar]:bg-chart-2/10"
                />
              </div>
            </div>
            <div className="flex flex-wrap gap-4 text-xs">
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-destructive/10 rounded" />
                <span>Overdue ({projectHealth.overdue})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-chart-4/10 rounded" />
                <span>Blocked ({projectHealth.blocked})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-chart-5/10 rounded" />
                <span>At Risk ({projectHealth.atRisk})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-chart-5/10 rounded" />
                <span>Needs Update ({projectHealth.needsUpdate})</span>
              </div>
              <div className="flex items-center gap-1">
                <div className="w-3 h-3 bg-chart-2/10 rounded" />
                <span>Healthy ({projectHealth.healthy})</span>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Task List */}
      <Card className={WIDGET_PANEL_CLASS}>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>
                {showAll ? 'All Tasks' : 'Tasks Requiring Attention'}
              </CardTitle>
              <CardDescription>
                {filteredTasks.length} task{filteredTasks.length !== 1 ? 's' : ''} {filter !== 'all' && `(${filter})`}
              </CardDescription>
            </div>
            {/* Filter Buttons */}
            <div className="flex items-center gap-2">
              <Filter className={`h-4 w-4 ${WIDGET_MUTED_TEXT_CLASS}`} />
              <Button
                variant={filter === 'all' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilter('all')}
              >
                All
              </Button>
              <Button
                variant={filter === 'overdue' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilter('overdue')}
              >
                Overdue
              </Button>
              <Button
                variant={filter === 'at-risk' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilter('at-risk')}
              >
                At Risk
              </Button>
              <Button
                variant={filter === 'needs-update' ? 'default' : 'ghost'}
                size="sm"
                onClick={() => setFilter('needs-update')}
              >
                Needs Update
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTasks.length === 0 ? (
            <div className="text-center py-12">
              <TrendingUp className={`mx-auto mb-3 h-12 w-12 ${WIDGET_ALERT_SUCCESS_TEXT_CLASS}`} />
              <h3 className="text-lg font-semibold text-muted-foreground mb-1">All Good!</h3>
              <p className={`text-sm ${WIDGET_MUTED_TEXT_CLASS}`}>
                {filter === 'all'
                  ? 'No tasks require attention at this time.'
                  : `No ${filter} tasks found.`}
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredTasks.map(task => (
                <Card
                  key={task.id}
                  className={`${WIDGET_PANEL_CLASS} cursor-pointer transition-shadow hover:shadow-md`}
                  onClick={() => handleTaskClick(task)}
                >
                  <CardContent className="pt-4">
                    <div className="flex items-start gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-2">
                          <h4 className="font-semibold text-muted-foreground">{task.title}</h4>
                          <TaskHealthBadge task={task} size="sm" />
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm text-muted-foreground">
                          <div className="flex items-center gap-1">
                            <UserIcon className="w-3 h-3" />
                            {task.assigneeIds.length > 0
                              ? task.assigneeIds.map(id => getUserName(id)).join(', ')
                              : 'Unassigned'}
                          </div>
                          
                          {task.targetEndDate && (
                            <div className="flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              Due: {new Date(task.targetEndDate).toLocaleDateString()}
                            </div>
                          )}

                          <div className="flex items-center gap-1">
                            <Clock className="w-3 h-3" />
                            Updated: {new Date(task.updatedAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                      
                      <Button variant="ghost" size="sm">
                        <ExternalLink className="w-4 h-4" />
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}